export type EventCallback = (payload?: any) => void;

/**
 * Generic publish/subscribe event bus.
 *
 * Extracted verbatim from GameApp, which used to implement this itself
 * (onEvent/fireEvent/listenersMap) despite it having zero game-specific
 * logic — it's a general-purpose event emitter that happened to live
 * inside the 1,400-line domain class. GameApp still exposes onEvent/
 * fireEvent as its own public API (unchanged for every existing call site
 * across the app, e.g. index.vue, GameStatus.vue, game.ts, game-tasks.ts)
 * but now just delegates to an instance of this class.
 *
 * See GameApp.fireEvent's doc comment for the full list of event names in
 * use (START_GAME, HIT_CUE_BALL, OPPONENT_HIT_CUE_BALL, PLAYER_CHANGE, OK,
 * END_TURN, PASS_BACK, END_GAME, CONCEDE_GAME, OPEN_DONATE, OPEN_HELP,
 * OPEN_SETTINGS, etc.) — that list lives with GameApp since it's the
 * game's own event vocabulary, not something this generic class should know.
 */
export class GameEventBus {
  private listenersMap: { [event: string]: EventCallback[] } = {};

  on(event: string, callback: EventCallback): void {
    const listeners = this.listenersMap[event];
    if (listeners === undefined) {
      this.listenersMap[event] = [callback];
    } else {
      try {
        this.listenersMap[event].push(callback);
      } catch (e) {
        console.error(e);
      }
    }
  }

  fire(event: string, payload?: any): void {
    const listeners = this.listenersMap[event];
    if (listeners !== undefined) {
      listeners.forEach((cb) => cb(payload));
    }
  }
}
